import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection,
  AutoTotal,
  parseMoney,
  sumMoney,
  formatMoney
} from '@/components/WorkbookPage';
import { CalendarClock } from 'lucide-react';

export const Rule6Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule6 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule6', { ...data, [field]: value });
  };
  
  const [expenses, setExpenses] = useState(data.irregularExpenses || [
    { name: 'Car registration/tags', annual: '', monthly: '' },
    { name: 'Holiday gifts', annual: '', monthly: '' },
    { name: 'Back-to-school supplies', annual: '', monthly: '' },
    { name: 'Car maintenance', annual: '', monthly: '' },
    { name: 'Medical (glasses, dental)', annual: '', monthly: '' },
    { name: 'Home repairs', annual: '', monthly: '' },
    { name: 'Birthdays', annual: '', monthly: '' },
    { name: '', annual: '', monthly: '' },
  ]);
  
  const updateExpense = (index, field, value) => {
    const newExpenses = [...expenses];
    newExpenses[index] = { ...newExpenses[index], [field]: value };
    if (field === 'annual') {
      newExpenses[index].monthly = (parseMoney(value) / 12).toFixed(2);
    }
    setExpenses(newExpenses);
    updateFormData('rule6', {
      ...data,
      irregularExpenses: newExpenses,
      monthlySetAside: (sumMoney(newExpenses, 'annual') / 12).toFixed(2)
    });
  };

  const totalSetAside = sumMoney(expenses, 'annual') / 12;
  
  return (
    <WorkbookPage pageId="rule-6" prevPage="rule-5" nextPage="rule-7">
      <RuleHeader ruleNumber="6" title="Plan for Irregular Expenses" section="control" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Irregular expenses are costs that don't happen every month but ARE predictable—car 
        registration, holiday gifts, school supplies, annual subscriptions, car maintenance. 
        They're not emergencies; they're expected. The problem is, we often forget about them 
        until they're due.
      </Paragraph>
      
      <SectionTitle>Why It Matters</SectionTitle>
      <Paragraph>
        When a $300 car registration hits and you haven't planned for it, it feels like an 
        emergency. But it wasn't unexpected—you just forgot. By setting aside money monthly 
        for these expenses, they become non-events.
      </Paragraph>
      
      <Callout type="info" title="The Math">
        <p>Annual expense ÷ 12 = monthly set-aside amount</p>
        <p className="text-sm mt-2">Example: $600/year car maintenance ÷ 12 = $50/month</p>
      </Callout>
      
      <ExampleBox title="Common Irregular Expenses">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>• Car registration/tags</div>
          <div>• Holiday gifts</div>
          <div>• Back-to-school</div>
          <div>• Car maintenance</div>
          <div>• Medical/dental</div>
          <div>• Home repairs</div>
          <div>• Annual subscriptions</div>
          <div>• Pet vet visits</div>
          <div>• Birthday gifts</div>
          <div>• Clothing replacement</div>
        </div>
      </ExampleBox>
      
      <StepList steps={[
        "List all expenses that happen once or a few times per year.",
        "Estimate the annual cost of each.",
        "Divide by 12 to get monthly amounts.",
        "Add up all monthly amounts—this is what you need to set aside.",
        "Create a separate account or envelope for these 'sinking funds.'",
        "Transfer this amount each month.",
        "When the expense comes due, the money is already there."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">
          <CalendarClock className="h-5 w-5 inline mr-2" />
          Your Irregular Expenses Planner
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-4">Expense</th>
                <th className="text-right py-2 px-2">Annual Cost</th>
                <th className="text-right py-2 pl-2">Monthly Set-Aside</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense, index) => (
                <tr key={index} className="border-b border-border/50">
                  <td className="py-2 pr-4">
                    <input
                      type="text"
                      className="w-full bg-transparent focus:outline-none"
                      placeholder="Expense name"
                      value={expense.name}
                      onChange={(e) => updateExpense(index, 'name', e.target.value)}
                    />
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="text"
                      className="w-full text-right bg-transparent focus:outline-none"
                      placeholder="$___"
                      value={expense.annual}
                      onChange={(e) => updateExpense(index, 'annual', e.target.value)}
                    />
                  </td>
                  <td className="py-2 pl-2 text-right">
                    <span className="text-foreground tabular-nums" data-testid={'rule6-monthly-' + index}>
                      {parseMoney(expense.annual) > 0 ? formatMoney(parseMoney(expense.annual) / 12) : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="mt-6 bg-secondary rounded-lg p-4">
          <AutoTotal
            label="Total monthly set-aside needed:"
            value={totalSetAside}
            hint="Each row is annual cost ÷ 12, added together"
            testId="rule6-total-setaside"
          />
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I've listed my irregular expenses
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I've calculated how much to set aside monthly
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I have a plan for where to keep this money
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Irregular expenses are predictable, not emergencies",
        "Annual cost ÷ 12 = monthly set-aside",
        "Create a 'sinking fund' for these expenses",
        "When the bill comes, you're ready"
      ]} />
      
      <AffirmationBox>
        I plan ahead so that predictable expenses never catch me off guard. I am prepared.
      </AffirmationBox>
      
      <OptionalSection>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="Write anything that comes to mind..."
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
